import { Fragment, useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { H3LocalBadge } from "../echoflicks/H3LocalBadge";
import { LtxLocalBadge } from "../echoflicks/LtxLocalBadge";
import { useMediaUrl } from "../echoflicks/useMediaUrl";
import {
  computePlaneLabel,
  ltx25SceneCount,
  type ProviderEcho,
} from "./providerExperimentTypes";

function EchoPreview({ echo }: { echo: ProviderEcho }) {
  const media = useMediaUrl({
    storage_bucket: echo.final_video_s3_bucket,
    storage_path: echo.final_video_s3_key,
  });
  return media.url ? (
    <video
      controls
      preload="metadata"
      src={media.url}
      onError={media.refresh}
      className="max-h-96 w-full rounded bg-black"
    />
  ) : (
    <p role="status">{media.error ?? "Loading private video…"}</p>
  );
}

export function ProviderEchoTable({ echoes }: { echoes: ProviderEcho[] }) {
  const [preview, setPreview] = useState<string | null>(null);
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {[
            "Echo",
            "Route",
            "Generated clips",
            "Compute",
            "Status",
            "Customer rating",
            "Video",
          ].map((label) => (
            <TableHead key={label}>{label}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {echoes.map((echo) => {
          const summary = echo.i2v_provider_summary;
          return (
            <Fragment key={echo.workload_id}>
              <TableRow>
                <TableCell>
                  <Link
                    className="font-medium underline"
                    to={`/video-sessions/${encodeURIComponent(echo.workload_id)}/show`}
                  >
                    {echo.title || echo.workload_id}
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    {new Date(echo.created_at).toLocaleString()}
                  </p>
                </TableCell>
                <TableCell>
                  {summary?.has_h3 || ltx25SceneCount(summary) ? (
                    <>
                      <H3LocalBadge summary={summary} compact />
                      <LtxLocalBadge summary={summary} compact />
                    </>
                  ) : (
                    "Veo control"
                  )}
                  <p className="text-xs text-muted-foreground">{echo.scope}</p>
                </TableCell>
                <TableCell>
                  {summary?.evidence === "selected_manifest"
                    ? `${summary.h3_scene_count ?? 0}/${summary.generated_scene_count ?? 0} H3 · ${summary.fallback_scene_count ?? 0} fallback · ${summary.static_scene_count ?? 0} static`
                    : "Evidence pending"}
                </TableCell>
                <TableCell>
                  {computePlaneLabel(summary?.compute_plane)}
                </TableCell>
                <TableCell>{echo.status}</TableCell>
                <TableCell>
                  {echo.rating ? `${echo.rating} / 5` : "Unrated"}
                  {echo.rating_comment && (
                    <p className="max-w-64 whitespace-pre-wrap text-xs text-muted-foreground">
                      {echo.rating_comment}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  {echo.final_video_s3_key && (
                    <Button
                      size="sm"
                      variant="outline"
                      aria-expanded={preview === echo.workload_id}
                      onClick={() =>
                        setPreview(
                          preview === echo.workload_id
                            ? null
                            : echo.workload_id,
                        )
                      }
                    >
                      {preview === echo.workload_id ? "Close" : "Play"}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
              {preview === echo.workload_id && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EchoPreview echo={echo} />
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
